import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { usePipeline } from "@/lib/sales";
import { STATUS } from "@/design/tokens";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SelectField } from "@/lib/crm";

const TONES = ["neutral", "blue", "amber", "brand", "green", "red"].map((k) => ({ key: k, label: k }));

const StageRow = ({ s, idx, onMove, onSave, onActive, onDelete, isFirst, isLastOpen }) => {
  const [f, setF] = useState({ name: s.name, probability: s.probability, tone: s.tone });
  const dirty = f.name !== s.name || Number(f.probability) !== s.probability || f.tone !== s.tone;
  const closing = s.outcome !== "open";
  return (
    <li className="flex flex-col gap-3 py-3 md:flex-row md:items-center" data-testid={`pipeline-stage-${s.key}`}>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" disabled={closing || isFirst} onClick={() => onMove(idx, -1)} data-testid={`pipeline-up-${s.key}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" disabled={closing || isLastOpen} onClick={() => onMove(idx, 1)} data-testid={`pipeline-down-${s.key}`}><ArrowDown className="h-4 w-4" /></Button>
        <span className={`ml-1 h-2.5 w-2.5 rounded-full ${STATUS[f.tone]?.dot}`} />
      </div>
      <Input className="md:w-56" maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`pipeline-name-${s.key}`} />
      <div className="flex items-center gap-2"><Input type="number" min="0" max="100" className="w-24" disabled={closing} value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} data-testid={`pipeline-prob-${s.key}`} /><span className="text-sm text-slate-500">%</span></div>
      <SelectField className="md:w-32" value={f.tone} onChange={(v) => setF({ ...f, tone: v })} options={TONES} testId={`pipeline-tone-${s.key}`} />
      <div className="flex flex-1 items-center gap-3 md:justify-end">
        {closing && <StatusBadge tone={s.outcome === "won" ? "green" : "red"} dot={false}>Fase di chiusura</StatusBadge>}
        <span className="text-xs text-slate-500" data-testid={`pipeline-usage-${s.key}`}>{s.in_use} opp.</span>
        <Switch checked={s.active} disabled={closing} onCheckedChange={(v) => onActive(s, v)} data-testid={`pipeline-active-${s.key}`} />
        <Button size="sm" disabled={!dirty} onClick={() => onSave(s, { name: f.name, probability: closing ? undefined : Number(f.probability), tone: f.tone })} data-testid={`pipeline-save-${s.key}`}>Salva</Button>
        <Button size="icon" variant="ghost" disabled={closing || s.in_use > 0} title={s.in_use ? "Fase utilizzata: puoi solo disattivarla" : "Elimina"} onClick={() => onDelete(s)} data-testid={`pipeline-delete-${s.key}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </li>
  );
};

export default function AdminPipeline() {
  const { stages, reload } = usePipeline();
  const [nf, setNf] = useState({ name: "", probability: 50 });
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errorMessage(e)); } };
  const openCount = stages.filter((s) => s.outcome === "open").length;
  const move = (i, dir) => {
    const ids = stages.map((s) => s.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    run(() => api.put("/sales/pipeline/order", { ids }), "Ordine aggiornato");
  };

  return (
    <div data-testid="admin-pipeline-page">
      <PageHeader overline="Amministrazione" title="Pipeline commerciale" description="Fasi, ordine e probabilità predefinite. Le fasi già utilizzate si disattivano, non si cancellano." />
      <Panel className="px-5 py-2"><ul className="divide-y divide-slate-100">
        {stages.map((s, i) => <StageRow key={`${s.id}-${s.updated_at}`} s={s} idx={i} isFirst={i === 0} isLastOpen={i === openCount - 1} onMove={move}
          onSave={(st, body) => run(() => api.patch(`/sales/pipeline/${st.id}`, body), "Fase aggiornata")}
          onActive={(st, v) => run(() => api.post(`/sales/pipeline/${st.id}/active`, { active: v }), v ? "Fase attivata" : "Fase disattivata")}
          onDelete={(st) => run(() => api.delete(`/sales/pipeline/${st.id}`), "Fase eliminata")} />)}
      </ul></Panel>
      <Panel className="mt-6 flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5"><p className="text-xs font-medium text-slate-600">Nuova fase (inserita prima di Vinta/Persa)</p>
          <Input maxLength={40} placeholder="Es. Sopralluogo" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="pipeline-new-name" /></div>
        <div className="flex items-center gap-2"><Input type="number" min="0" max="100" className="w-24" value={nf.probability} onChange={(e) => setNf({ ...nf, probability: e.target.value })} data-testid="pipeline-new-prob" /><span className="text-sm text-slate-500">%</span></div>
        <Button className="gap-1" disabled={nf.name.trim().length < 2} onClick={() => run(() => api.post("/sales/pipeline", { name: nf.name.trim(), probability: Number(nf.probability) }), "Fase creata").then(() => setNf({ name: "", probability: 50 }))} data-testid="pipeline-new-save"><Plus />Aggiungi fase</Button>
      </Panel>
    </div>
  );
}
